import { useEffect, useState, useMemo } from "react";
import { TrendingUp, Coins, Repeat, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/analytics?months=12")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const maxValue = useMemo(() => {
    if (!data?.series?.length) return 1;
    return Math.max(1, ...data.series.map((s) => s.total));
  }, [data]);

  if (loading) return <div className="bg-white rounded-2xl p-8 text-center text-[#64748B]" data-testid="analytics-loading">Chargement...</div>;
  if (!data) return <div className="bg-white rounded-2xl p-12 text-center text-[#64748B]">Aucune donnée.</div>;

  const fmt = (n) => n.toLocaleString("fr-FR", { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Collecté total"
          value={`${fmt(data.overall_total)} €`}
          Icon={Coins}
          testid="analytics-kpi-total"
        />
        <KpiCard
          label="Nombre de dons"
          value={fmt(data.overall_count)}
          Icon={Users}
          testid="analytics-kpi-count"
        />
        <KpiCard
          label="Souscriptions actives"
          value={fmt(data.active_subscriptions)}
          Icon={Repeat}
          testid="analytics-kpi-subs"
        />
        <KpiCard
          label="Don moyen"
          value={
            data.overall_count > 0
              ? `${fmt(data.overall_total / data.overall_count)} €`
              : "—"
          }
          Icon={TrendingUp}
          testid="analytics-kpi-avg"
        />
      </div>

      {/* Bar chart — last 12 months */}
      <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8" data-testid="analytics-chart">
        <h4 className="font-serif text-xl text-[#0A192F] font-semibold mb-1">Évolution des dons</h4>
        <p className="text-sm text-[#64748B] mb-8">12 derniers mois</p>

        <div className="grid grid-cols-12 gap-2 md:gap-3 items-end h-56">
          {data.series.map((s, i) => {
            const heightPct = maxValue > 0 ? Math.max(2, (s.total / maxValue) * 100) : 2;
            const monthLabel = formatMonth(s.month);
            return (
              <div
                key={s.month}
                className="flex flex-col items-center gap-2 group"
                data-testid={`analytics-bar-${i}`}
              >
                <div className="text-[10px] text-[#002B49] font-medium opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">
                  {fmt(s.total)} €
                </div>
                <div
                  className="w-full bg-gradient-to-t from-[#002B49] to-[#C5A059] rounded-t-md transition-all hover:opacity-90"
                  style={{ height: `${heightPct}%` }}
                  title={`${s.month} — ${fmt(s.total)} € (${s.count} dons)`}
                />
                <div className="text-[10px] text-[#64748B] uppercase tracking-wider">
                  {monthLabel}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Type breakdown */}
      <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8" data-testid="analytics-by-type">
        <h4 className="font-serif text-xl text-[#0A192F] font-semibold mb-1">Répartition par type</h4>
        <p className="text-sm text-[#64748B] mb-6">Tous dons complétés</p>

        <div className="space-y-5">
          <TypeRow
            label="Dons ponctuels"
            count={data.by_type?.one_time?.count || 0}
            total={data.by_type?.one_time?.total || 0}
            color="#002B49"
            allTotal={data.overall_total || 1}
          />
          <TypeRow
            label="Dons mensuels"
            count={data.by_type?.monthly?.count || 0}
            total={data.by_type?.monthly?.total || 0}
            color="#C5A059"
            allTotal={data.overall_total || 1}
          />
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, Icon, testid }) => (
  <div className="bg-white rounded-2xl border border-black/5 p-5 flex items-start justify-between gap-3" data-testid={testid}>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-medium">{label}</div>
      <div className="font-serif text-xl md:text-2xl text-[#002B49] mt-1.5 font-semibold truncate">{value}</div>
    </div>
    <div className="w-9 h-9 rounded-lg bg-[#FDF8EE] flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-[#C5A059]" />
    </div>
  </div>
);

const TypeRow = ({ label, count, total, color, allTotal }) => {
  const fmt = (n) => n.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
  const pct = Math.round((total / allTotal) * 100);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-medium text-[#0A192F] text-sm">{label}</span>
        <span className="text-sm text-[#64748B]">
          {count} dons — <strong className="text-[#002B49]">{fmt(total)} €</strong>
        </span>
      </div>
      <div className="h-2 bg-[#F3F0EA] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

function formatMonth(ym) {
  // ym = "2026-05"
  const [_y, m] = ym.split("-");
  const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  return months[parseInt(m, 10) - 1] || ym;
}

export default Analytics;
